import { FC, useRef, useState } from "react";

import { Box, Stack } from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormDialog } from "@/forms-design-system/FormDialog";
import { useExpandableContentHeight } from "@/forms-design-system/FormExpandableText/useExpandableContentHeight";
import { FormTextButton } from "@/forms-design-system/FormTextButton";
import { ArrowDiagonalRight } from "@/icons/outlined";

const CLAMPED_LINE_COUNT = 3;
const CROPPED_LAST_LINE_FADE = `linear-gradient(to bottom, black ${((CLAMPED_LINE_COUNT - 1) / CLAMPED_LINE_COUNT) * 100}%, transparent)`;

interface OrganizationMissionProps {
  missionHtml: string;
}

export const OrganizationMission: FC<OrganizationMissionProps> = ({ missionHtml }) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isCropped } = useExpandableContentHeight(contentRef, { initialIsCropped: false });

  return (
    <>
      <Stack alignItems="flex-start" gap={1}>
        <Box
          ref={contentRef}
          data-test="organization-description"
          sx={(theme: Theme) => ({
            ...getClampedTypographyStyles(theme, "body1"),
            [theme.breakpoints.down("sm")]: getClampedTypographyStyles(theme, "body2"),
            ...getMissionContentStyles(theme),
            color: theme.palette.forms.text.moderate,
            overflow: "hidden",
            width: "100%",
            ...(isCropped && { maskImage: CROPPED_LAST_LINE_FADE }),
          })}
          dangerouslySetInnerHTML={{ __html: missionHtml }}
        />
        {isCropped && (
          <FormTextButton
            data-test="organization-description-more-details"
            endIcon={<ArrowDiagonalRight />}
            onClick={() => setIsDialogOpen(true)}
          >
            {t("common", "seeMoreDetails")}
          </FormTextButton>
        )}
      </Stack>
      <FormDialog
        title={t("organization", "ourMission")}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        size="s"
        variant={isSmallScreen ? "drawer" : "default"}
      >
        <Box
          sx={(theme: Theme) => ({
            ...getMissionContentStyles(theme),
            ["& *"]: {
              maxWidth: "100%",
              color: "inherit !important",
            },
          })}
          dangerouslySetInnerHTML={{ __html: missionHtml }}
        />
      </FormDialog>
    </>
  );
};

const getClampedTypographyStyles = (theme: Theme, variant: "body1" | "body2") => ({
  ...theme.typography[variant],
  maxHeight: `calc(${theme.typography[variant].lineHeight} * ${CLAMPED_LINE_COUNT})`,
});

const getMissionContentStyles = (theme: Theme) => ({
  overflowWrap: "anywhere" as const,
  "& p": { margin: 0 },
  "& ul, & ol": { margin: 0, paddingLeft: theme.spacing(3) },
  "& img": { maxWidth: "100%", height: "auto" },
  "& .lxcl-bold": { fontWeight: theme.typography.fontWeightBold },
  "& .lxcl-italic": { fontStyle: "italic" },
  "& .lxcl-underline": { textDecoration: "underline" },
  "& .lxcl-strikethrough": { textDecoration: "line-through" },
});
